// Which categories an account can see in the sidebar and on /sanctuary.
//
// Every account (free or Pro) sees only the categories of its active region,
// plus the shared ones every region has. Pro switches region from /journey.

import { getUserPlan } from "./planAccess";

export const ASTRIA_COUNTRY_CATEGORIES = [
  "Astria US",
  "Astria Spanish",
  "Astria Spanish V2",
  "Astria Japan",
  "Astria Korea",
  "Astria Korea V2",
  "Astria Korea V3",
  "Astria Korea Talk",
  "Astria Korea Hybrid",
  "Astria Japan V2",
  "Astria Japan Talk",
  "Astria Japan V3",
  "Astria Japan Hybrid",
  "Astria Brazil",
  "Astria Brazil V2",
  "social compatibility",
  "Astria Mexico",
  "Astria Mexico V2",
  "Astria India",
  "Astria India V2",
  "Astria India V3",
  "Astria PSM",
  "Astria Philippines",
  "Astria Philippines V2",
  "Astria Singapore",
  "Astria Singapore V2",
  "Astria Singapore V3",
  "Astria Malaysia",
  "Astria Malaysia V2",
  "Astria Malaysia V3",
  "Astria Indonesia",
  "Astria Indonesia V2",
  "Astria Indonesia V3",
  "Astria Indonesia Talk",
  "Astria Vietnam V2",
  "Astria Vietnam",
  "Astria UK",
  "Astria UK V2",
  "Astria Canada",
  "Astria Canada V2",
  "Astria Canada V3",
  "Astria GCC",
  "Astria GCC V2",
  "Astria Italy",
  "Astria Germany",
  "Astria China",
  "Astria Russia",
  "Astria France",
];

export const REGION_TO_ASTRIA: Record<string, string[]> = {
  indonesia: [
    "Astria Indonesia",
    "Astria Indonesia V2",
    "Astria Indonesia V3",
    "Astria Indonesia Talk",
  ],
  vietnam: ["Astria Vietnam V2", "Astria Vietnam"],
  canada: ["Astria Canada", "Astria Canada V2", "Astria Canada V3"],
  uk: ["Astria UK", "Astria UK V2"],
  gcc: ["Astria GCC", "Astria GCC V2"],
  malaysia: ["Astria Malaysia", "Astria Malaysia V2", "Astria Malaysia V3"],
  singapore: ["Astria Singapore", "Astria Singapore V2", "Astria Singapore V3"],
  philippines: ["Astria Philippines", "Astria Philippines V2"],
  brazil: ["Astria Brazil", "Astria Brazil V2", "social compatibility"],
  mexico: ["Astria Mexico", "Astria Mexico V2"],
  korea: [
    "Astria Korea",
    "Astria Korea V2",
    "Astria Korea V3",
    "Astria Korea Talk",
    "Astria Korea Hybrid",
  ],
  india: ["Astria India", "Astria India V2", "Astria India V3"],
  japan: [
    "Astria Japan",
    "Astria Japan V2",
    "Astria Japan Talk",
    "Astria Japan V3",
    "Astria Japan Hybrid",
  ],
  spanish: ["Astria Spanish", "Astria Spanish V2"],
  us: ["Astria US"],
  italy: ["Astria Italy"],
  germany: ["Astria Germany"],
  china: ["Astria China"],
  russia: ["Astria Russia"],
  france: ["Astria France"],
};

export const HEALJAI_ONLY_CATEGORIES = new Set([
  "Emotions",
  "HealJai Talk",
  "Healing Horoscope V1",
  "Healing Horoscope V2",
  "Healing Horoscope V3",
  "Healing Horoscope",
  "รหัส Healjai V3",
  "Energy Match",
]);

export const ALLOWED_ALL_REGION_EMAILS = new Set([
  "Koiespecial@yahoo.com",
  "encrypteddev4@gmail.com",
]);

export const BUSSINESS_EMAIL = new Set(["encrypteddev7@gmail.com"]);
export const BUSSINESS_ASTRO_CATEGORIES = ["MateScan", "Energy Match V3", "CheckMate"];
export const BUSINESS_ONLY_CATEGORIES = new Set(["CheckMate"]);

export const astriaCountryCategorySet = new Set(ASTRIA_COUNTRY_CATEGORIES);

interface CategoryLike {
  name: string;
  isActive: boolean;
}

interface UserLike {
  email?: string;
  region?: string;
  roleId?: number;
  allRegionsApproved?: boolean;
  plan?: string;
  /** Region opened by a running Global Pass, and when it ends (epoch ms). */
  globalPassRegion?: string | null;
  globalPassExpiresAt?: number | null;
}

export const getVisibleCategories = <T extends CategoryLike>(
  categories: T[],
  user: UserLike | null | undefined,
): T[] => {
  const active = categories.filter((c) => c.isActive);
  const userRegion = user?.region || "healjai";
  const userEmail = user?.email || "";
  const isTester = user?.roleId === 3;

  // A running Global Pass also lists one other country (the server checks the clock
  // and refuses the content once it ends).
  const passRegion =
    user?.globalPassRegion && (user.globalPassExpiresAt ?? 0) > Date.now()
      ? user.globalPassRegion
      : null;
  const isPassCategory = (name: string) =>
    !!passRegion &&
    (passRegion === "healjai"
      ? HEALJAI_ONLY_CATEGORIES.has(name)
      : !!REGION_TO_ASTRIA[passRegion]?.includes(name));

  // Only region's own category + shared ones; no other region's, no
  // Healjai-only and no business-only categories.
  const ownRegionOnly = () => {
    const allowedAstria = REGION_TO_ASTRIA[userRegion];
    return active.filter((c) => {
      const isAllowedAstria = allowedAstria?.includes(c.name) || isPassCategory(c.name);
      if (isAllowedAstria) return true;
      if (astriaCountryCategorySet.has(c.name)) return false;
      if (HEALJAI_ONLY_CATEGORIES.has(c.name)) return false;
      if (BUSINESS_ONLY_CATEGORIES.has(c.name)) return false;
      return true;
    });
  };

  // Healjai (Thailand) → everything except other Astria regional categories.
  const healjaiOnly = () =>
    active.filter(
      (c) =>
        isPassCategory(c.name) ||
        (!astriaCountryCategorySet.has(c.name) &&
          !BUSINESS_ONLY_CATEGORIES.has(c.name)),
    );

  // Testers: restricted to their assigned region.
  if (isTester) return userRegion === "healjai" ? healjaiOnly() : ownRegionOnly();

  // Allow-listed emails and "All Regions" approved accounts see everything.
  if (
    ALLOWED_ALL_REGION_EMAILS.has(userEmail) ||
    (userRegion === "all" && user?.allRegionsApproved) ||
    getUserPlan(user) === "global"
  ) {
    return active;
  }

  if (BUSSINESS_EMAIL.has(userEmail)) {
    return active.filter((c) => BUSSINESS_ASTRO_CATEGORIES.includes(c.name));
  }

  // Free and Pro alike: only the active region's categories. Pro unlocks every
  // region on /journey, and switching there updates user.region, which is what
  // this filter reads.
  return userRegion === "healjai" ? healjaiOnly() : ownRegionOnly();
};
